import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, Icon, Stack, Text } from "@chakra-ui/react";
import { FaStar } from "react-icons/fa";

// Accent for recommended candidates: the star by the name and the card's edge.
export const RECOMMENDED_ACCENT = "yellow.400";

// Without layout (jsdom) the reason can't be measured, so a character budget
// (or several lines) decides whether it would overflow the two-line clamp.
export const REASON_CLAMP_CHARS = 120;
const REASON_CLAMP_LINES = 2;

export function isRecommended(candidateReport) {
  return !!candidateReport?.recommended;
}

// "Recommended by Jane Doe"; without a name just "Recommended". The source
// (button / MCP) is read from the API but deliberately not shown.
export function recommendedByLine(candidateReport) {
  const by = candidateReport?.recommendedBy?.trim();
  return `Recommended${by ? ` by ${by}` : ""}`;
}

// Small accent star, shown next to a recommended candidate's name.
export function RecommendedStar({ boxSize = "12px" }) {
  return (
    <Icon
      as={FaStar}
      boxSize={boxSize}
      color={RECOMMENDED_ACCENT}
      flexShrink={0}
      aria-label="Recommended"
      role="img"
    />
  );
}

function fallbackIsLong(reason) {
  return reason.length > REASON_CLAMP_CHARS || reason.includes("\n");
}

// Whether the clamped reason actually overflows its two lines. Measured from
// the rendered element (scrollHeight vs clientHeight, re-checked on resize);
// with no layout (clientHeight 0, as in jsdom) it falls back to the length
// heuristic. Only measured while clamped: expanded, it never overflows.
function useReasonOverflows(ref, reason, enabled) {
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!enabled || !el) {
      return undefined;
    }
    const measure = () => {
      setOverflows(
        el.clientHeight > 0
          ? el.scrollHeight > el.clientHeight + 1
          : fallbackIsLong(reason)
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, reason, enabled]);

  return overflows;
}

// By-line + reason for a recommended candidate (the star sits by the name).
// `clamp` limits the reason to two lines, with a Show more / Show less toggle
// when it actually overflows (the list card); without it the whole reason
// wraps (the detail panel).
export default function RecommendedNote({ candidateReport, clamp = false, fontSize = "xs" }) {
  const [expanded, setExpanded] = useState(false);
  const reasonRef = useRef(null);
  const reason = candidateReport?.recommendedReason?.trim() || "";
  const recommended = isRecommended(candidateReport);
  const overflows = useReasonOverflows(
    reasonRef,
    reason,
    recommended && clamp && !!reason && !expanded
  );

  if (!recommended) {
    return null;
  }

  const clamped = clamp && !expanded;

  return (
    <Stack spacing="2px" minW={0} w="100%" data-testid="recommended-note">
      <Text fontSize={fontSize} color="whiteAlpha.600" noOfLines={1}>
        {recommendedByLine(candidateReport)}
      </Text>
      {reason && (
        <Box minW={0}>
          <Text
            ref={reasonRef}
            fontSize={fontSize}
            color="whiteAlpha.800"
            whiteSpace="pre-wrap"
            overflowWrap="anywhere"
            noOfLines={clamped ? REASON_CLAMP_LINES : undefined}
            data-testid="recommended-reason"
          >
            {reason}
          </Text>
          {clamp && (expanded || overflows) && (
            <Text
              as="button"
              type="button"
              fontSize={fontSize}
              color="teal.300"
              textDecoration="underline"
              onClick={(e) => {
                // Inside a clickable list row: toggling shouldn't select it.
                e.stopPropagation();
                setExpanded((value) => !value);
              }}
            >
              {expanded ? "Show less" : "Show more"}
            </Text>
          )}
        </Box>
      )}
    </Stack>
  );
}
